import { Component, OnInit, OnDestroy, HostListener, ViewContainerRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { InitializationStatusCodes, HttpProviderFactory } from 'portalServices';
import { Subscription } from 'rxjs';
import {
ActivatedRoute,
Event,
NavigationCancel,
NavigationEnd,
NavigationError,
NavigationStart,
Router
} from '@angular/router';
import { Location } from "@angular/common";
import { AppServices } from './servicelayer/app-services';
import { FormService } from './common/clientSerivces/form.service';
import { NgIdleService, IdlePopupComponent, LoadingComponent } from 'shared-components';
import { appRouteConstants } from './app.route.constants';
import { AppInitializationService } from './common/services/app-initialization.service';
import { RouteAuthenticationService } from './common/services/route-auth.service';
import { filter, debounceTime } from 'rxjs/operators';

@Component({
    selector: "app-portal-web-client",
    standalone: true,
    imports: [CommonModule, RouterOutlet, IdlePopupComponent, LoadingComponent],
    template: `
        <div class="app-container">
            <!-- Idle Popup Component -->
            <app-idle-popup [onLogout]="handleLogout.bind(this)"></app-idle-popup>

            <!-- Loading Component (Shared) -->
            <app-loading #loadingComponent></app-loading>

            <!-- Main App Content -->
            <div class="main-content">
                 <router-outlet></router-outlet>
            </div>
        </div>
    `,
    styles: [`
        .app-container {
            position: relative;
            width: 100%;
            height: 100vh;
        }

        .main-content {
            width: 100%;
            height: 100%;
        }
    `],
    providers: []
})
export class AppComponent implements OnInit, OnDestroy {
    @HostListener('window:beforeunload', ['$event']) beforeUnloadHandler(): void { this.beforeUnload(); }
    @HostListener('window:unload', ['$event']) unloadHandler(): void { this.unload(); }
    @HostListener('copy', ['$event']) onCopy(event: ClipboardEvent): void { event.preventDefault(); }

    @ViewChild('loadingComponent') loadingComponent: LoadingComponent;

    private authServicesInitialized = false;
    private idleServiceInitialized = false;

    private networkErrorSub: Subscription;
    private spinnerSub: Subscription;
    private routerSub: Subscription;
    private authChangeSub: Subscription;

    constructor(
        private router: Router,
        private appServices: AppServices,
        private location: Location,
        private containerRef: ViewContainerRef,
        private route: ActivatedRoute,
        private formService: FormService,
        private idleService: NgIdleService,
        private appInitService: AppInitializationService,
        private routeAuthService: RouteAuthenticationService
    ) {
        console.log('AppComponent: Angular 18 + ServiceLib initialized');

        // Initialize services
        this.initializeServices();
        this.setupEventHandlers();
    }

    private initializeServices(): void {
        // NOTE: this runs in the constructor, before the Router has parsed the
        // initial navigation, so this.router.url is still '/'. Use the browser
        // location directly so deep-link routes (e.g. /join/joining-form?token=...)
        // are detected correctly.
        const currentUrl = `${window.location.pathname}${window.location.search}`;
        const isJoinRoute = currentUrl.includes('join');
        const isLoginRoute = currentUrl.includes('login');

        console.log('AppComponent: Initializing services for URL:', currentUrl);
        console.log('AppComponent: Is join route?', isJoinRoute);
        console.log('AppComponent: Is login route?', isLoginRoute);

        // Always initialize auth services for login routes, skip only for join routes
        if (!isJoinRoute) {
            // Initialize authentication service (this triggers ServiceLib initialization)
            console.log('AppComponent: Initializing authentication services for non-join route');
            this.appServices.authenticationService;
            this.routeAuthService.initializeAuthenticationTracking();

            // Subscribe to authentication changes using our RouteAuthenticationService
            this.subscribeToAuthenticationChanges();
        } else {
            console.log('AppComponent: Skipping authentication service initialization for join route');
        }

        // Setup spinner service (this doesn't trigger getProfile calls)
        this.spinnerSub = this.appServices.spinnerService.getSpinnerObserver().subscribe((status: any) => {
            if (this.loadingComponent) {
                Promise.resolve().then(() => {
                    if (status === 'start') {
                        this.loadingComponent.show();
                    } else {
                        this.loadingComponent.hide();
                    }
                });
            }
        });

        const isAuthRoute = currentUrl.includes('login') || currentUrl.includes('join') || currentUrl === '/';
        if (isAuthRoute) {
            this.appServices.spinnerService.resetSpinner();
        }
    }

    private subscribeToAuthenticationChanges(): void {
        if (this.authServicesInitialized) {
            console.log('AppComponent: Authentication services already initialized');
            return;
        }

        this.authServicesInitialized = true;

        // Only handle significant authentication state changes (login/logout)
        let initialAuthState: boolean | null = null;

        this.authChangeSub = this.routeAuthService.isAuthenticated$.subscribe(isAuthenticated => {
            console.log('AppComponent: Authentication state changed to:', isAuthenticated);
            console.log('AppComponent: Current URL:', this.router.url);

            // Track initial state to avoid reacting to initialization
            if (initialAuthState === null) {
                initialAuthState = isAuthenticated;
                console.log('AppComponent: Initial auth state set to:', initialAuthState);
                return;
            }

            // Only react to actual changes (login/logout), not initialization
            if (initialAuthState !== isAuthenticated) {
                console.log('AppComponent: Significant auth change detected:', initialAuthState, '->', isAuthenticated);

                // For login (false -> true), always handle the change
                // For logout (true -> false), always handle the change  
                // Don't restrict based on current route for login success
                if (isAuthenticated) {
                    // User just logged in - handle regardless of current route
                    console.log('AppComponent: User logged in - handling authentication change');
                    this.appInitService.handleAuthenticationChange(isAuthenticated);
                } else {
                    // User logged out - handle regardless of current route
                    console.log('AppComponent: User logged out - handling authentication change');
                    this.appInitService.handleAuthenticationChange(isAuthenticated);
                }

                initialAuthState = isAuthenticated;
            }
        });
    }

    private setupEventHandlers(): void {
        // Handle network errors (401 unauthorized)
        this.networkErrorSub = HttpProviderFactory.getInstance().onNetworkError.subscribe((error) => {
            if (error && error.code === 401) {
                const currentUrl = this.router.url;
                console.log('AppComponent: 401 error detected. Current URL:', currentUrl);
                console.log('AppComponent: URL includes join?', currentUrl.includes('join'));

                // Ignore 401s while the user is on login/join/root routes.
                if (currentUrl.includes('join') || currentUrl.includes('login') || currentUrl === '/') {
                    console.log('AppComponent: 401 on auth route - ignoring session timeout');
                    return;
                }

                console.warn('AppComponent: 401 Unauthorized - session expired, triggering timeout');
                this.appInitService.handleSessionTimeout();
            } else if (error) {
                console.log('AppComponent: Network error (not 401):', error);
            }
        });

        // Handle router events for spinner
        this.routerSub = this.router.events.subscribe((event: Event) => {
            const currentUrl = this.router.url;
            const isAuthRoute = currentUrl.includes('login') || currentUrl.includes('join') || currentUrl === '/';

            switch (true) {
                case event instanceof NavigationStart: {
                    this.appServices.spinnerService.requestStarted();
                    break;
                }
                case event instanceof NavigationEnd: {
                    this.appServices.spinnerService.requestEnded();

                    if (isAuthRoute) {
                        this.appServices.spinnerService.resetSpinner();
                    }

                    // Safety net: After navigation completes, ensure spinner is properly reset
                    // This prevents infinite spinner on page refresh when guards/HTTP calls
                    // may have caused counter misalignment
                    setTimeout(() => {
                        if (!isAuthRoute) {
                            // Only reset if we're not actively making requests
                            // Check if there are any pending HTTP calls by checking if another
                            // navigation or HTTP request is in progress
                            const spinnerObserver = this.appServices.spinnerService.getSpinnerObserver();
                            let currentSpinnerState: string;
                            const sub = spinnerObserver.subscribe(state => currentSpinnerState = state);
                            sub.unsubscribe();

                            // If spinner is still showing after navigation settled, reset it
                            if (currentSpinnerState === 'start') {
                                console.warn('AppComponent: Spinner still active after navigation settled - resetting to prevent infinite loading');
                                this.appServices.spinnerService.resetSpinner();
                            }
                        }
                    }, 1000); // Wait 1 second for any pending requests to complete

                    // Handle idle service based on new route
                    this.handleIdleServiceBasedOnRoute();
                    break;
                }
                case event instanceof NavigationCancel:
                case event instanceof NavigationError: {
                    // Navigation was cancelled or failed - always reset spinner to prevent infinite loop
                    console.warn('AppComponent: Navigation cancelled or error - resetting spinner');
                    this.appServices.spinnerService.requestEnded();
                    this.appServices.spinnerService.resetSpinner();
                    break;
                }
                default: {
                    break;
                }
            }
        });

        // Initialize form service metadata when app starts
        this.formService.getFormData();

        // DON'T call handleIdleServiceBasedOnRoute() here - router not ready yet
    }

    private handleIdleServiceBasedOnRoute(): void {
        const currentUrl = this.router.url;
        const isJoinRoute = currentUrl.includes('join');
        const isLoginRoute = currentUrl.includes('login');

        console.log('AppComponent: Checking route:', currentUrl, 'isJoinRoute:', isJoinRoute, 'isLoginRoute:', isLoginRoute);

        if (isJoinRoute || isLoginRoute) {
            // Disable idle service for join/login routes - NEVER initialize on these routes
            console.log('AppComponent: On join/login route - disabling idle service');
            this.idleServiceInitialized = false;
            this.idleService.stopTimer();
            this.idleService.removeActionFromStore();
            this.idleService.USER_IDLE_TIMER_VALUE_IN_MIN = 0;
            this.idleService.FINAL_LEVEL_TIMER_VALUE_IN_MIN = 0;
        } else {
            // Enable idle service for authenticated routes
            // Only initialize ONCE on first authenticated route
            if (!this.idleServiceInitialized && !this.idleService.USER_IDLE_TIMER_VALUE_IN_MIN) {
                // Initialize idle service for authenticated routes
                console.log('AppComponent: Initializing idle service for authenticated routes');
                this.idleServiceInitialized = true;
                this.idleService.USER_IDLE_TIMER_VALUE_IN_MIN = 15; // 15 minutes inactivity timeout
                this.idleService.FINAL_LEVEL_TIMER_VALUE_IN_MIN = 1; // 1 minute warning countdown
                this.idleService.initilizeSessionTimeout();
            } else if (this.idleServiceInitialized) {
                console.log('AppComponent: Idle service already initialized, skipping re-initialization');
            }
        }
    }

    ngOnInit(): void {
        console.log('AppComponent: ngOnInit - ServiceLib integration successful');
        this.formService.getFormData();

        // Check initial route AFTER a delay to let routing stabilize
        setTimeout(() => {
            console.log('AppComponent: Checking initial route after stabilization');
            this.handleIdleServiceBasedOnRoute();
        }, 500);
    }

    ngOnDestroy(): void {
        // Clean up subscriptions
        if (this.networkErrorSub) { this.networkErrorSub.unsubscribe(); }
        if (this.spinnerSub) { this.spinnerSub.unsubscribe(); }
        if (this.routerSub) { this.routerSub.unsubscribe(); }
        if (this.authChangeSub) { this.authChangeSub.unsubscribe(); }
    }

    beforeUnload(): void {
        // ServiceLib handles cleanup
    }

    unload(): void {
        console.log('AppComponent: Page unload');
        // ServiceLib handles cleanup
    }

    /**
     * Handles logout when idle session timer expires.
     * Called by IdlePopupComponent when user doesn't respond to inactivity warning.
     */
    handleLogout(): void {
        console.log('AppComponent: Handling logout from idle session');

        this.idleService.stopTimer();
        this.idleService.removeActionFromStore();

        try {
            // Call authentication service logout
            this.appServices.authenticationService.logout().subscribe(
                (response: any) => {
                    console.log('AppComponent: Logout successful');
                    this.appServices.logout();
                    window.location.href = '/';
                },
                (error: any) => {
                    console.error('AppComponent: Logout error', error);
                    // Fallback to simple redirect
                    window.location.href = '/';
                }
            );
        } catch (error) {
            console.error('AppComponent: Error during logout', error);
            // Fallback to simple redirect
            window.location.href = '/';
        }
    }
}